import React, { useEffect, useState, useCallback } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { FiEye, FiMessageCircle, FiMail, FiBell, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { API_BASE } from "../../config";
import Loader from "./Loader";

const COLORS = ["#0f5132", "#14532d", "#1a6b45", "#2d8a5e", "#4caf7d", "#7bcf9f", "#a8e6c3", "#d4f5e3"];

const STAT_CARDS = [
  { key: "totalPageViews", label: "Unique Visitors", icon: FiEye, bg: "#e8f5e9", iconColor: "#0f5132" },
  { key: "totalInquiries", label: "Inquiries", icon: FiMessageCircle, bg: "#e0f2f1", iconColor: "#00695c" },
  { key: "totalMessages", label: "Messages", icon: FiMail, bg: "#e3f2fd", iconColor: "#1565c0" },
  { key: "unreadMessages", label: "Unread", icon: FiBell, bg: "#fce4ec", iconColor: "#c62828" },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="analytics-tooltip">
      <p className="analytics-tooltip-label">{label || payload[0].name}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || "#333" }}>
          {entry.name || "Count"}: <strong>{entry.value}</strong>
        </p>
      ))}
    </div>
  );
}

function DashboardHome({ token, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Drill-down state
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownData, setDrilldownData] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState("");
  const [drilldownKey, setDrilldownKey] = useState(null);

  // Recent data state
  const [recentMessages, setRecentMessages] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Fetch analytics summary
    fetch(`${API_BASE}/analytics/summary`, { headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load dashboard");
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    // Fetch recent messages
    fetch(`${API_BASE}/contact?limit=5`, { headers: authHeaders })
      .then((res) => res.json())
      .then((json) => setRecentMessages(json.data || []))
      .catch(() => {});

    // Fetch recent products
    fetch(`${API_BASE}/products?limit=5`, { headers: authHeaders })
      .then((res) => res.json())
      .then((json) => setRecentProducts(json.data || []))
      .catch(() => {});
  }, [token]);

  const fetchDrilldown = useCallback(async (type, params) => {
    // Get the value for the key
    const value = params.page || params.product || params.date;
    const key = `${type}:${value}`;

    // Toggle off if clicking the same element again
    if (drilldownKey === key) {
      setDrilldown(null);
      setDrilldownData([]);
      setDrilldownTitle("");
      setDrilldownKey(null);
      return;
    }

    setDrilldownLoading(true);
    setDrilldown(type);
    setDrilldownKey(key);
    try {
      let url;
      if (type === "page") {
        url = `${API_BASE}/analytics/drilldown/pageviews?page=${encodeURIComponent(value)}`;
        setDrilldownTitle(`Daily views: ${value}`);
      } else if (type === "product") {
        url = `${API_BASE}/analytics/drilldown/inquiries?product=${encodeURIComponent(value)}`;
        setDrilldownTitle(`Daily inquiries: ${value}`);
      } else if (type === "date") {
        url = `${API_BASE}/analytics/drilldown/views-by-date?date=${encodeURIComponent(value)}`;
        setDrilldownTitle(`Pages viewed on ${value}`);
      }
      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) throw new Error("Failed to load drill-down data");
      const json = await res.json();
      setDrilldownData(json.data || []);
    } catch (err) {
      console.error("Drilldown error:", err);
      setDrilldownData([]);
      setDrilldownTitle("Error loading data");
    }
    setDrilldownLoading(false);
  }, [token, drilldownKey]);

  const goBack = () => {
    setDrilldown(null);
    setDrilldownData([]);
    setDrilldownTitle("");
    setDrilldownKey(null);
  };

  if (loading) return <Loader />;
  if (error) return <p style={{ color: "#b02a2a" }}>{error}</p>;
  if (!data) return <p>No data available.</p>;

  const pieData = (data.viewsByPage || []).map((v) => ({ name: v._id, value: v.count }));
  const barData = (data.inquiriesByProduct || []).map((i) => ({
    name: i._id.length > 12 ? i._id.substring(0, 12) + "…" : i._id,
    fullName: i._id,
    count: i.count,
  }));
  const lineData = (data.viewsTrend || []).map((v) => ({
    date: v._id.substring(5),
    fullDate: v._id,
    views: v.count,
  }));

  const drillBarData = drilldownData.map((d) => ({ name: d._id, count: d.count }));

  return (
    <div className="dashboard-home">
      {/* Stats Cards */}
      <div className="dashboard-stats">
        {STAT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.key} className="dashboard-stat-card" style={{ background: card.bg }}>
              <div className="dashboard-stat-top">
                <p className="dashboard-stat-label">{card.label}</p>
                <div className="dashboard-stat-icon">
                  <Icon size={22} color={card.iconColor} />
                </div>
              </div>
              <h3 className="dashboard-stat-count" style={{ color: card.iconColor }}>
                {data[card.key] ?? 0}
              </h3>
            </div>
          );
        })}
      </div>

      {/* Quick Charts */}
      <div className="dashboard-charts">
        <div className="dashboard-chart-card">
          <h3>Views by Page</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  onClick={(entry) => {
                    // Recharts Pie onClick: entry = { name, value, ... }
                    const pageName = entry && entry.name;
                    if (pageName) fetchDrilldown("page", { page: pageName });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="dashboard-empty">No data yet</p>
          )}
        </div>

        <div className="dashboard-chart-card">
          <h3>Views Trend (30 days)</h3>
          {lineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="views"
                  stroke="#0f5132"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#0f5132", cursor: "pointer" }}
                  activeDot={{ r: 6, cursor: "pointer", onClick: (e, payload) => {
                    // activeDot onClick: payload = { payload: { date, fullDate, views } }
                    const dateValue = payload && payload.payload && payload.payload.fullDate;
                    if (dateValue) fetchDrilldown("date", { date: dateValue });
                  }}}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="dashboard-empty">No data yet</p>
          )}
        </div>

        <div className="dashboard-chart-card">
          <h3>Inquiries by Product</h3>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="count"
                  fill="#0f5132"
                  radius={[4, 4, 0, 0]}
                  onClick={(entry) => {
                    // Recharts Bar onClick: entry = { payload: { name, fullName, count } }
                    const productName = entry && entry.payload && entry.payload.fullName;
                    if (productName) fetchDrilldown("product", { product: productName });
                  }}
                  style={{ cursor: "pointer" }}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="dashboard-empty">No data yet</p>
          )}
        </div>
      </div>

      {/* Drill-down Panel */}
      {drilldown && (
        <div className="dashboard-drilldown">
          <div className="dashboard-drilldown-header">
            <h3>{drilldownTitle}</h3>
            <button className="btn-secondary" onClick={goBack}>
              <FiArrowLeft size={14} /> Back
            </button>
          </div>
          {drilldownLoading ? (
            <Loader />
          ) : drillBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={drillBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eaeaea" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#1a6b45" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="dashboard-empty">No data found for this selection.</p>
          )}
        </div>
      )}

      {/* Recent Activity */}
      <div className="dashboard-activity">
        <div className="dashboard-activity-card">
          <div className="activity-card-header">
            <h3>Recent Messages</h3>
            <button className="activity-view-all" onClick={() => onNavigate && onNavigate("messages")}>
              View all <FiArrowRight size={14} />
            </button>
          </div>
          {recentMessages.length > 0 ? (
            <div className="dashboard-activity-list">
              {recentMessages.map((m) => (
                <div key={m._id} className={`dashboard-activity-item ${m.read ? "" : "unread"}`}>
                  <div className="activity-item-header">
                    <strong>{m.name}</strong>
                    <span>{new Date(m.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p>{m.message.substring(0, 80)}{m.message.length > 80 ? "…" : ""}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="dashboard-empty">No messages yet</p>
          )}
        </div>

        <div className="dashboard-activity-card">
          <div className="activity-card-header">
            <h3>Recent Products</h3>
            <button className="activity-view-all" onClick={() => onNavigate && onNavigate("products")}>
              View all <FiArrowRight size={14} />
            </button>
          </div>
          {recentProducts.length > 0 ? (
            <div className="dashboard-activity-list">
              {recentProducts.map((p) => (
                <div key={p._id} className="dashboard-activity-item">
                  <div className="activity-item-header">
                    <strong>{p.name}</strong>
                    <span className="pm-cat">{p.category}</span>
                  </div>
                  <p>{p.description.substring(0, 80)}{p.description.length > 80 ? "…" : ""}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="dashboard-empty">No products yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;
